// src/components/User/Calendar.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  CalendarMonth as CalendarMonthIcon,
  Today as TodayIcon,
  Event as TomorrowIcon,
  DateRange as DateRangeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DragIndicator as DragIndicatorIcon,
  Place as PlaceIcon,
  DirectionsCar as DirectionsCarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import GooglePlacesAutocompleteNative from '../Common/GooglePlacesAutocompleteNative';

interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  location?: string;
  start: {
    dateTime?: string;
    date?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
  };
  status: string;
  htmlLink?: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus?: string;
  }>;
  extendedProperties?: {
    private?: {
      [key: string]: string;
    };
    shared?: {
      [key: string]: string;
    };
  };
  recurringEventId?: string;
  recurrence?: string[];
}

interface Task {
  id: string;
  title: string;
  completed: boolean;
  dueDate?: string;
  notes?: string;
  googleTaskId?: string;
  isRecurring?: boolean;
  recurrenceInfo?: string;
  parentId?: string;
}

interface TaskList {
  id: string;
  title: string;
}

interface EventType {
  value: string;
  label: string;
  icon?: React.ReactNode;
  defaultLocation?: string;
}

const EVENT_TYPES: EventType[] = [
  { value: 'call', label: 'Call', defaultLocation: 'Phone' },
  { value: 'out_of_office', label: 'Out of Office Meeting', defaultLocation: '' },
  { value: 'in_office', label: 'In My Office Meeting', defaultLocation: 'My Office' },
  { value: 'zoom', label: 'Zoom', defaultLocation: 'Zoom Meeting' },
  { value: 'meet', label: 'Google Meet', defaultLocation: 'Google Meet' },
  { value: 'other', label: 'Other', defaultLocation: '' },
];

const Calendar: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [dateLoading, setDateLoading] = useState(false);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskLists, setTaskLists] = useState<TaskList[]>([]);
  const [selectedTaskList, setSelectedTaskList] = useState<string>('@default');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [newTaskDialog, setNewTaskDialog] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [editTaskDialog, setEditTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [editTaskDueDate, setEditTaskDueDate] = useState('');
  const [editTaskListId, setEditTaskListId] = useState('@default');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [newEventDialog, setNewEventDialog] = useState(false);
  const [newEventType, setNewEventType] = useState('');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventEndDate, setNewEventEndDate] = useState('');
  const [newEventStartTime, setNewEventStartTime] = useState('');
  const [newEventEndTime, setNewEventEndTime] = useState('');
  const [newEventDuration, setNewEventDuration] = useState(60); // Default 1 hour in minutes
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventAttendees, setNewEventAttendees] = useState<string[]>([]);
  const [newEventTransparency, setNewEventTransparency] = useState<'opaque' | 'transparent'>('opaque'); // opaque = busy, transparent = free
  const [savingEvent, setSavingEvent] = useState(false);
  const [editEventDialog, setEditEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [editEventTitle, setEditEventTitle] = useState('');
  const [editEventDescription, setEditEventDescription] = useState('');
  const [editEventLocation, setEditEventLocation] = useState('');
  const [editEventDate, setEditEventDate] = useState('');
  const [editEventEndDate, setEditEventEndDate] = useState('');
  const [editEventStartTime, setEditEventStartTime] = useState('');
  const [editEventEndTime, setEditEventEndTime] = useState('');
  const [editEventDuration, setEditEventDuration] = useState(60); // Default 1 hour in minutes
  const [editEventAttendees, setEditEventAttendees] = useState<string[]>([]);
  const [editEventTransparency, setEditEventTransparency] = useState<'opaque' | 'transparent'>('opaque');
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [contactsSearch, setContactsSearch] = useState('');
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [leaveEarlyMinutes, setLeaveEarlyMinutes] = useState(15);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [hasOfficeAddress, setHasOfficeAddress] = useState<boolean | null>(null);
  const [conflictDialog, setConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState<any>(null);
  const [timeUpdatedMessage, setTimeUpdatedMessage] = useState('');
  const [taskFilter, setTaskFilter] = useState<'all' | 'today' | 'tomorrow' | 'pastdue' | 'nodue'>('all');
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [taskOrder, setTaskOrder] = useState<Record<string, string[]>>({});
  const [eventLocations, setEventLocations] = useState<{[eventId: string]: string}>({});

  // Load task order and event locations from localStorage on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem('taskOrder');
    if (savedOrder) {
      try {
        setTaskOrder(JSON.parse(savedOrder));
      } catch (e) {
        console.error('Failed to parse saved task order:', e);
      }
    }
    
    const savedLocations = localStorage.getItem('eventLocations');
    if (savedLocations) {
      try {
        setEventLocations(JSON.parse(savedLocations));
      } catch (e) {
        console.error('Failed to parse saved event locations:', e);
      }
    }
  }, []);

  // Save task order to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(taskOrder).length > 0) {
      localStorage.setItem('taskOrder', JSON.stringify(taskOrder));
    }
  }, [taskOrder]);

  // Save event locations to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(eventLocations).length > 0) {
      localStorage.setItem('eventLocations', JSON.stringify(eventLocations));
    }
  }, [eventLocations]);

  // Get user's current location and update it periodically
  useEffect(() => {
    const updateLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const newLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setUserLocation(newLocation);
            console.log('User location:', position.coords);
            
            // Send location to backend
            if (isAuthenticated) {
              try {
                await api.get(`/endpoints/calendar?action=update-location&lat=${newLocation.lat}&lng=${newLocation.lng}`);
              } catch (err) {
                console.error('Failed to update location:', err);
              }
            }
          },
          (error) => {
            console.error('Error getting location:', error);
          },
          { enableHighAccuracy: true }
        );
      }
    };
    
    // Update location immediately
    updateLocation();
    
    // Update location every 5 minutes
    const interval = setInterval(updateLocation, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    checkAuthStatus();
    
    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    
    if (error === 'redirect_uri_mismatch') {
      setError('Google OAuth Error: The redirect URI "' + window.location.origin + '/portal/calendar" needs to be added to your Google Cloud Console OAuth client configuration.');
      window.history.replaceState({}, document.title, '/portal/calendar');
    } else if (code) {
      // Check if we've already processed this code
      const processedCode = sessionStorage.getItem('oauth_processed_code');
      if (processedCode === code) {
        console.log('OAuth code already processed, skipping');
        window.history.replaceState({}, document.title, '/portal/calendar');
      } else {
        // Mark this code as processed
        sessionStorage.setItem('oauth_processed_code', code);
        handleAuthCallback(code);
      }
    }
    
  }, []);
  
  // Set up periodic auth status checks in a separate effect
  useEffect(() => {
    if (isAuthenticated) {
      const authCheckInterval = setInterval(() => {
        console.log('Periodic auth check to refresh token if needed');
        checkAuthStatus();
      }, 10 * 60 * 1000); // Check every 10 minutes
      
      return () => clearInterval(authCheckInterval);
    }
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/calendar?action=auth-status');
      console.log('Auth status response:', response.data);
      console.log('Current member from auth context:', member);
      
      if (response.data.success) {
        setIsAuthenticated(response.data.data.authenticated);
        setClientId(response.data.data.clientId);
        
        // Check if user has office address
        if (response.data.data.hasOfficeAddress !== undefined) {
          setHasOfficeAddress(response.data.data.hasOfficeAddress);
        }
        
        if (response.data.data.authenticated) {
          console.log('User is authenticated, fetching calendar data');
          await fetchCalendarData();
        } else {
          // Not authenticated, stop loading
          setLoading(false);
        }
      } else {
        // API returned success: false
        setLoading(false);
        setError('Failed to check authentication status');
      }
    } catch (err: any) {
      console.error('Failed to check auth status:', err);
      if (err.response?.data) {
        console.error('Error response:', err.response.data);
      }
      // Set a default client ID if API fails
      setClientId('523086767285-18n9hfths3pbfdkuo2mp959fl8c00g25.apps.googleusercontent.com');
      setLoading(false);
      setError('Failed to connect to calendar service');
    }
  };

  const fetchCalendarData = async (date?: Date) => {
    // Don't show loading spinner for date changes, only for initial load
    if (!events.length && !tasks.length) {
      setLoading(true);
    } else {
      setDateLoading(true);
    }
    setError(null);
    try {
      const targetDate = date || selectedDate;
      const dateParam = formatDateLocal(targetDate);
      
      const [eventsRes, tasksRes, listsRes] = await Promise.all([
        api.get(`/endpoints/calendar?action=events&date=${dateParam}`),
        api.get(`/endpoints/calendar?action=tasks&listId=${selectedTaskList}`),
        api.get('/endpoints/calendar?action=task-lists')
      ]);

      console.log('Events response:', eventsRes.data);
      console.log('Tasks response:', tasksRes.data);
      console.log('Task lists response:', listsRes.data);
      
      if (eventsRes.data.success) {
        const eventData = eventsRes.data.data;
        setEvents(eventData);
        
        // Store event locations in localStorage
        const newLocations: {[eventId: string]: string} = {};
        eventData.forEach((event: CalendarEvent) => {
          if (event.location) {
            newLocations[event.id] = event.location;
          }
        });
        
        // Merge with existing locations
        setEventLocations(prev => ({
          ...prev,
          ...newLocations
        }));
      }
      if (tasksRes.data.success) {
        console.log('Setting tasks state with:', tasksRes.data.data);
        setTasks(tasksRes.data.data);
      }
      if (listsRes.data.success) {
        setTaskLists(listsRes.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
      setDateLoading(false);
    }
  };

  const handleGoogleConnect = () => {
    // First check if we're still authenticated
    console.log('Current auth context:', { member });
    
    if (!member) {
      setError('You must be logged in to connect Google Calendar');
      return;
    }
    
    setIsConnecting(true);
    
    // Google OAuth2 URL
    const redirectUri = encodeURIComponent(window.location.origin + '/portal/calendar');
    const scope = encodeURIComponent('https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/tasks https://www.googleapis.com/auth/contacts.readonly');
    const responseType = 'code';
    const accessType = 'offline';
    const prompt = 'consent'; // Force consent screen to ensure refresh token is returned
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${redirectUri}&` +
      `response_type=${responseType}&` +
      `scope=${scope}&` +
      `access_type=${accessType}&` +
      `prompt=${prompt}`;
    
    console.log('Google OAuth URL:', authUrl); // Debug log
    console.log('Client ID:', clientId); // Debug log
    
    // Redirect to Google OAuth
    window.location.href = authUrl;
  };

  const handleAuthCallback = async (code: string) => {
    console.log('Processing OAuth callback with code:', code.substring(0, 20) + '...');
    
    // Prevent double submission by clearing the URL immediately
    window.history.replaceState({}, document.title, '/portal/calendar');
    
    // Check if we're already processing
    if (isConnecting) {
      console.log('Already processing OAuth callback, ignoring duplicate');
      return;
    }
    
    setIsConnecting(true);
    try {
      const response = await api.post('/endpoints/calendar?action=auth', {
        code: code,
        redirect_uri: window.location.origin + '/portal/calendar'
      });
      
      console.log('OAuth response:', response.data);
      
      if (response.data.success) {
        setIsAuthenticated(true);
        setIsConnecting(false);
        fetchCalendarData();
      }
    } catch (err: any) {
      console.error('OAuth error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to authenticate with Google';
      
      // Check if it's a redirect_uri_mismatch error
      if (window.location.search.includes('error=redirect_uri_mismatch')) {
        setError('Google OAuth Error: The redirect URI needs to be added to Google Cloud Console. Please contact your administrator.');
      } else {
        setError(errorMessage);
      }
      
      setIsConnecting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCalendarData();
    setRefreshing(false);
  };

  // Clean up task order by removing tasks that no longer exist
  const cleanupTaskOrder = (currentTasks: Task[]) => {
    const taskIds = new Set(currentTasks.map(t => t.id));
    const newTaskOrder: Record<string, string[]> = {};
    
    Object.entries(taskOrder).forEach(([key, order]) => {
      const cleanedOrder = order.filter(id => taskIds.has(id));
      if (cleanedOrder.length > 0) {
        newTaskOrder[key] = cleanedOrder;
      }
    });
    
    if (JSON.stringify(newTaskOrder) !== JSON.stringify(taskOrder)) {
      setTaskOrder(newTaskOrder);
    }
  };

  const handleToggleTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const originalCompleted = task.completed;

    try {
      // Optimistically update UI
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, completed: !originalCompleted } : t
      ));

      // Update in Google Tasks
      const response = await api.put(`/endpoints/calendar?action=update-task&taskId=${taskId}&listId=${selectedTaskList}`, {
        completed: !originalCompleted
      });

      if (!response.data.success) {
        // Revert on failure
        setTasks(tasks.map(t => 
          t.id === taskId ? { ...t, completed: originalCompleted } : t
        ));
        console.error('Failed to update task in Google Tasks');
      } else if (!originalCompleted) {
        // If task was marked as completed, remove it from the custom order
        const newTaskOrder = { ...taskOrder };
        Object.keys(newTaskOrder).forEach(key => {
          newTaskOrder[key] = newTaskOrder[key].filter(id => id !== taskId);
        });
        setTaskOrder(newTaskOrder);
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      // Revert on error
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, completed: originalCompleted } : t
      ));
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await api.delete(`/endpoints/calendar?action=delete-task&taskId=${taskId}&listId=${selectedTaskList}`);
      setTasks(tasks.filter(t => t.id !== taskId));
      
      // Remove from custom order
      const newTaskOrder = { ...taskOrder };
      Object.keys(newTaskOrder).forEach(key => {
        newTaskOrder[key] = newTaskOrder[key].filter(id => id !== taskId);
      });
      setTaskOrder(newTaskOrder);
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;

    try {
      const response = await api.post('/endpoints/calendar?action=create-task', {
        title: newTaskTitle,
        dueDate: newTaskDueDate || null,
        listId: selectedTaskList
      });

      if (response.data.success) {
        setTasks([...tasks, response.data.data]);
        setNewTaskTitle('');
        setNewTaskDueDate('');
        setNewTaskDialog(false);
      }
    } catch (err) {
      console.error('Failed to add task:', err);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setEditTaskTitle(task.title);
    setEditTaskDueDate(task.dueDate || '');
    setEditTaskListId(selectedTaskList); // Use current list as default
    setEditTaskDialog(true);
  };

  const handleSaveEditTask = async () => {
    if (!editingTask || !editTaskTitle.trim()) return;

    console.log('Saving task:', {
      id: editingTask.id,
      title: editTaskTitle,
      dueDate: editTaskDueDate,
      fromList: selectedTaskList,
      toList: editTaskListId
    });

    try {
      // If the task is being moved to a different list
      if (editTaskListId !== selectedTaskList) {
        // Delete from current list
        await api.delete(`/endpoints/calendar?action=delete-task&taskId=${editingTask.id}&listId=${selectedTaskList}`);
        
        // Create in new list
        const createResponse = await api.post('/endpoints/calendar?action=create-task', {
          title: editTaskTitle,
          dueDate: editTaskDueDate || null,
          listId: editTaskListId,
          completed: editingTask.completed
        });
        
        if (createResponse.status === 200 || createResponse.status === 201) {
          // Switch to the new list to see the moved task
          setSelectedTaskList(editTaskListId);
        }
      } else {
        // Just update the task in the same list
        const response = await api.put(`/endpoints/calendar?action=update-task&taskId=${editingTask.id}&listId=${selectedTaskList}`, {
          title: editTaskTitle,
          dueDate: editTaskDueDate || null,
          completed: editingTask.completed
        });

        if (response.status !== 200) {
          console.error('Update failed with status:', response.status);
          alert('Failed to update task. Please try again.');
          return;
        }
      }

      // Close the dialog
      setEditTaskDialog(false);
      setEditingTask(null);
      setEditTaskTitle('');
      setEditTaskDueDate('');
      setEditTaskListId('@default');
      setDatePickerOpen(false);
      
      // Refresh tasks from Google
      await fetchTasks();
      
      console.log('Task updated successfully');
    } catch (err: any) {
      console.error('Failed to update task:', err);
      console.error('Error response:', err.response?.data);
      alert('Failed to update task. Please try again.');
    }
  };

  const setQuickDate = (type: 'today' | 'tomorrow' | 'custom') => {
    const date = new Date();

    // Debug logging
    console.log('setQuickDate - Current date:', date.toString());
    console.log('setQuickDate - Current date ISO:', date.toISOString());
    console.log('setQuickDate - Current local date:', formatDateLocal(date));
    console.log('setQuickDate - Timezone offset:', date.getTimezoneOffset());

    switch (type) {
      case 'today':
        setEditTaskDueDate(formatDateLocal(date));
        console.log('setQuickDate - Today set to:', formatDateLocal(date));
        break;
      case 'tomorrow':
        date.setDate(date.getDate() + 1);
        setEditTaskDueDate(formatDateLocal(date));
        console.log('setQuickDate - Tomorrow set to:', formatDateLocal(date));
        break;
      case 'custom':
        setDatePickerOpen(true);
        break;
    }
  };

  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    if (!startTime) return '';
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    
    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
    
    return `${endHours}:${endMinutes}`;
  };

  const formatEventTime = (event: CalendarEvent) => {
    let timeStr = '';
    if (event.start.dateTime) {
      const startTime = new Date(event.start.dateTime);
      const endTime = new Date(event.end.dateTime!);
      timeStr = `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      timeStr = 'All day';
    }
    
    // Add location if available (use cached location if event doesn't have it)
    const location = event.location || eventLocations[event.id];
    if (location) {
      // For travel events, just show the time
      if (event.summary?.startsWith('🚗 Travel to')) {
        return timeStr;
      }
      // Truncate location if too long
      const shortLocation = location.length > 40 ? location.substring(0, 37) + '...' : location;
      return `${timeStr} • ${shortLocation}`;
    }
    
    return timeStr;
  };

  const getEventStatus = (event: CalendarEvent) => {
    const now = new Date();
    const eventStart = new Date(event.start.dateTime || event.start.date!);
    const eventEnd = new Date(event.end.dateTime || event.end.date!);

    if (now >= eventStart && now <= eventEnd) {
      return 'ongoing';
    } else if (now < eventStart) {
      return 'upcoming';
    } else {
      return 'past';
    }
  };

  // Fetch tasks when selected task list changes
  useEffect(() => {
    if (isAuthenticated && selectedTaskList) {
      fetchTasks();
    }
  }, [selectedTaskList, isAuthenticated]);

  // Fetch events when selectedDate changes  
  useEffect(() => {
    if (isAuthenticated) {
      fetchCalendarData(selectedDate);
    }
  }, [selectedDate, isAuthenticated]);

  const fetchTasks = async () => {
    try {
      const response = await api.get(`/endpoints/calendar?action=tasks&listId=${selectedTaskList}`);
      if (response.data.success) {
        const fetchedTasks = response.data.data;
        setTasks(fetchedTasks);
        // Clean up task order to remove any deleted tasks
        cleanupTaskOrder(fetchedTasks);
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const handleTaskListChange = (listId: string) => {
    setSelectedTaskList(listId);
  };

  const handleAddEvent = async () => {
    if (!newEventTitle.trim() || !newEventType || !newEventDate || !newEventStartTime || !newEventEndDate || !newEventEndTime) {
      alert('Please fill in all required fields');
      return;
    }

    if (savingEvent) {
      return; // Prevent double submission
    }

    setSavingEvent(true);
    try {
      const eventData: any = {
        summary: newEventTitle,
        description: newEventDescription,
        location: newEventLocation,
        start: {
          dateTime: `${newEventDate}T${newEventStartTime}:00`,
          timeZone: 'America/Toronto',
        },
        end: {
          dateTime: `${newEventEndDate}T${newEventEndTime}:00`,
          timeZone: 'America/Toronto',
        },
        attendees: newEventAttendees.map(email => ({ email })),
        transparency: newEventTransparency,
        eventType: newEventType,
      };
      
      // Add leave early minutes for out of office meetings
      if (newEventType === 'out_of_office') {
        eventData.leaveEarlyMinutes = leaveEarlyMinutes;
      }

      console.log('Creating event with data:', eventData);
      const response = await api.post('/endpoints/calendar?action=create-event', eventData);

      console.log('Event creation response:', response.data);
      if (response.data.success) {
        // Reset form
        setNewEventDialog(false);
        setNewEventTitle('');
        setNewEventType('');
        setNewEventDescription('');
        setNewEventLocation('');
        setNewEventDate('');
        setNewEventEndDate('');
        setNewEventStartTime('');
        setNewEventEndTime('');
        setNewEventAttendees([]);
        setNewEventTransparency('opaque');
        setLeaveEarlyMinutes(15);
        setTimeUpdatedMessage('');
        
        // Refresh events
        fetchCalendarData(selectedDate);
      }
    } catch (err: any) {
      console.error('Failed to create event:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create event';
      
      // Show conflict message in a more user-friendly way
      if (err.response?.status === 409) {
        console.log('409 Conflict Response:', err.response?.data);
        if (err.response?.data?.data) {
          // Conflict error with structured data
          console.log('Conflict data:', err.response.data.data);
          console.log('Suggested times:', err.response.data.data.suggestedTimes);
          setConflictData(err.response.data.data);
          setConflictDialog(true);
        } else {
          // Fallback for old-style conflict error
          alert(errorMessage);
        }
      } else {
        alert(`Failed to create event: ${errorMessage}`);
      }
      
      // Important: Do NOT close the dialog or refresh on error
      // The user needs to fix the issue and try again
      setSavingEvent(false); // Re-enable the save button
      return;
    } finally {
      setSavingEvent(false);
    }
  };

  const handleEventClick = async (event: CalendarEvent) => {
    // For travel time events, find and open the parent event
    if (event.summary?.startsWith('🚗 Travel to')) {
      // Extract parent event ID from extended properties if available
      const parentEventId = event.extendedProperties?.private?.parentEventId;
      if (parentEventId) {
        // Find the parent event in the current events list
        const parentEvent = events.find(e => e.id === parentEventId);
        if (parentEvent) {
          handleEventClick(parentEvent);
          return;
        }
      }
      // If we can't find the parent, just show an info message
      alert('This is a travel time event. Edit or delete the main event to modify travel time.');
      return;
    }
    
    // Parse the event details
    const startDateTime = new Date(event.start.dateTime || event.start.date || '');
    const endDateTime = new Date(event.end.dateTime || event.end.date || '');
    
    // Calculate duration in minutes
    const durationMs = endDateTime.getTime() - startDateTime.getTime();
    const durationMinutes = Math.round(durationMs / 60000);
    
    setEditingEvent(event);
    setEditEventTitle(event.summary || '');
    setEditEventDescription(event.description || '');
    setEditEventLocation(event.location || '');
    setEditEventDate(formatDateLocal(startDateTime));
    setEditEventEndDate(formatDateLocal(endDateTime));
    setEditEventStartTime(startDateTime.toTimeString().slice(0, 5));
    setEditEventEndTime(endDateTime.toTimeString().slice(0, 5));
    setEditEventDuration(durationMinutes > 0 ? durationMinutes : 60); // Default to 1 hour if invalid
    setEditEventAttendees(event.attendees?.map(a => a.email) || []);
    setEditEventTransparency((event as any).transparency || 'opaque');
    
    // Log for debugging
    console.log('Editing event:', {
      title: event.summary,
      location: event.location,
      date: formatDateLocal(startDateTime),
      startTime: startDateTime.toTimeString().slice(0, 5),
      endTime: endDateTime.toTimeString().slice(0, 5),
      duration: durationMinutes
    });
    
    setEditEventDialog(true);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent || !editEventTitle.trim()) {
      return;
    }

    if (savingEvent) {
      return;
    }

    setSavingEvent(true);
    try {
      // Check if date has changed
      const originalDate = formatDateLocal(new Date(editingEvent.start.dateTime || editingEvent.start.date || ''));
      const dateChanged = originalDate !== editEventDate;
      
      // If date changed and event has location, we need to handle travel events
      if (dateChanged && editEventLocation && editingEvent.extendedProperties?.private?.eventType === 'out_of_office') {
        console.log('Date changed for out-of-office event, handling travel events...');
        
        // Find and delete old travel events for the original date
        const travelEventId = editingEvent.extendedProperties?.private?.travelEventId;
        if (travelEventId) {
          try {
            console.log('Deleting old travel event:', travelEventId);
            await api.delete(`/endpoints/calendar?action=delete-event&eventId=${travelEventId}`);
          } catch (err) {
            console.error('Failed to delete old travel event:', err);
            // Continue even if deletion fails
          }
        }
      }
      
      const eventData: any = {
        summary: editEventTitle,
        description: editEventDescription,
        location: editEventLocation,
        start: {
          dateTime: `${editEventDate}T${editEventStartTime}:00`,
          timeZone: 'America/Toronto',
        },
        end: {
          dateTime: `${editEventEndDate}T${editEventEndTime}:00`,
          timeZone: 'America/Toronto',
        },
        attendees: editEventAttendees.map(email => ({ email })),
        transparency: editEventTransparency,
        // Preserve the event type if it exists
        eventType: editingEvent.extendedProperties?.private?.eventType,
        leaveEarlyMinutes: editingEvent.extendedProperties?.private?.leaveEarlyMinutes
      };

      const response = await api.put(`/endpoints/calendar?action=update-event&eventId=${editingEvent.id}`, eventData);

      if (response.data.success) {
        setEditEventDialog(false);
        setEditingEvent(null);
        // Fetch calendar data for both dates if date changed
        if (dateChanged) {
          // Refresh both the old and new dates
          const oldDate = new Date(originalDate);
          fetchCalendarData(oldDate);
          fetchCalendarData(new Date(editEventDate));
        } else {
          fetchCalendarData(selectedDate);
        }
      }
    } catch (err: any) {
      console.error('Failed to update event:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update event';
      
      if (err.response?.status === 409) {
        alert(errorMessage);
      } else {
        alert(`Failed to update event: ${errorMessage}`);
      }
      
      setSavingEvent(false);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent || deletingEvent) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${editingEvent.summary}"?`)) {
      return;
    }

    setDeletingEvent(true);
    try {
      const response = await api.delete(`/endpoints/calendar?action=delete-event&eventId=${editingEvent.id}`);

      if (response.data.success) {
        setEditEventDialog(false);
        setEditingEvent(null);
        fetchCalendarData(selectedDate);
      }
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      alert('Failed to delete event. Please try again.');
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleQuickDeleteTravel = async (event: CalendarEvent) => {
    if (!window.confirm(`Are you sure you want to delete this travel event?`)) {
      return;
    }

    try {
      const response = await api.delete(`/endpoints/calendar?action=delete-event&eventId=${event.id}`);

      if (response.data.success) {
        fetchCalendarData(selectedDate);
        // Show success message (optional)
        console.log('Travel event deleted successfully');
      }
    } catch (err: any) {
      console.error('Failed to delete travel event:', err);
      alert('Failed to delete travel event. Please try again.');
    }
  };

  const handleEventTypeChange = (type: string) => {
    setNewEventType(type);
    const eventType = EVENT_TYPES.find(et => et.value === type);
    if (eventType?.defaultLocation) {
      setNewEventLocation(eventType.defaultLocation);
    }
  };

  const searchContacts = async (query: string) => {
    if (query.length < 2) {
      setAvailableContacts([]);
      return;
    }

    try {
      const response = await api.get(`/endpoints/calendar?action=search-contacts&q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setAvailableContacts(response.data.data);
      }
    } catch (err) {
      console.error('Failed to search contacts:', err);
    }
  };

  const handleDateChange = (direction: 'prev' | 'next' | 'today') => {
    let newDate: Date;
    
    switch (direction) {
      case 'prev':
        newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'next':
        newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'today':
        newDate = new Date();
        // Reset time to start of day to avoid timezone issues
        newDate.setHours(0, 0, 0, 0);
        break;
    }
    
    setSelectedDate(newDate);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, task: Task, index: number) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight opacity to the dragged element
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset opacity
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedTask(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedTask) return;
    
    const filteredTasks = getFilteredTasks();
    const draggedIndex = filteredTasks.findIndex(t => t.id === draggedTask.id);
    
    if (draggedIndex === dropIndex) return;
    
    // Create a key for this list and filter combination
    const orderKey = `${selectedTaskList}-${taskFilter}`;
    
    // Get current order or create from filtered tasks
    const currentOrder = taskOrder[orderKey] || filteredTasks.map(t => t.id);
    
    // Remove dragged item and insert at new position
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedTask.id);
    
    // Update state
    setTaskOrder(prev => ({
      ...prev,
      [orderKey]: newOrder
    }));
    
    console.log('Task reordered locally. Order saved to localStorage.');
  };

  // Filter tasks based on selected filter
  const getFilteredTasks = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dayAfterTomorrow = new Date(tomorrow);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
    
    const filtered = tasks.filter(task => {
      if (task.completed) return false; // Don't show completed tasks
      
      if (taskFilter === 'all') return true;
      
      if (taskFilter === 'nodue') return !task.dueDate;
      
      if (!task.dueDate) return false; // No due date tasks only show in 'all' or 'nodue'

      // Add T12:00:00 to ensure the date is interpreted in local timezone
      const taskDate = new Date(task.dueDate + 'T12:00:00');
      taskDate.setHours(0, 0, 0, 0);
      
      switch (taskFilter) {
        case 'today':
          return taskDate.getTime() === today.getTime();
        case 'tomorrow':
          return taskDate.getTime() === tomorrow.getTime();
        case 'pastdue':
          return taskDate < today;
        default:
          return true;
      }
    });
    
    // Apply saved order if available
    const orderKey = `${selectedTaskList}-${taskFilter}`;
    const savedOrder = taskOrder[orderKey];
    
    if (savedOrder && savedOrder.length > 0) {
      // Sort tasks based on saved order
      const orderedTasks = [...filtered].sort((a, b) => {
        const aIndex = savedOrder.indexOf(a.id);
        const bIndex = savedOrder.indexOf(b.id);
        
        // If task is not in saved order, put it at the end
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });
      
      return orderedTasks;
    }
    
    return filtered;
  };
  
  const getTaskFilterCount = (filter: 'all' | 'today' | 'tomorrow' | 'pastdue' | 'nodue') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return tasks.filter(task => {
      if (task.completed) return false;
      
      if (filter === 'all') return true;
      
      if (filter === 'nodue') return !task.dueDate;
      
      if (!task.dueDate) return false; // Tasks without due date don't count for date-specific filters

      // Add T12:00:00 to ensure the date is interpreted in local timezone
      const taskDate = new Date(task.dueDate + 'T12:00:00');
      taskDate.setHours(0, 0, 0, 0);
      
      switch (filter) {
        case 'today':
          return taskDate.getTime() === today.getTime();
        case 'tomorrow':
          return taskDate.getTime() === tomorrow.getTime();
        case 'pastdue':
          return taskDate < today;
        default:
          return false;
      }
    }).length;
  };
  
  if (!isAuthenticated) {
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Calendar Integration
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Connect your Google Calendar to view events and manage tasks. You can use any Google account (Gmail, G Suite, or personal).
          </Typography>
        </Paper>

        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CalendarMonthIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Connect Google Calendar
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Sign in with any Google account to access your calendar events and tasks.
          </Typography>
          
          {!clientId && (
            <Alert severity="warning" sx={{ mb: 2, mx: 'auto', maxWidth: 500 }}>
              Loading Google configuration...
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2, mx: 'auto', maxWidth: 400 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleGoogleConnect}
              disabled={isConnecting || !clientId}
              startIcon={isConnecting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#4285f4',
                '&:hover': {
                  backgroundColor: '#357ae8',
                },
              }}
            >
              {isConnecting ? 'Connecting...' : 'Connect Google Calendar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (loading || isConnecting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1">
            Calendar
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              startIcon={refreshing ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={handleGoogleConnect}
              size="small"
            >
              Change Account
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Events List */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                selectedDate.toDateString() === new Date().toDateString() 
                  ? "Today's Events"
                  : `Events for ${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
              }
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<EventIcon />}
              action={
                <Box display="flex" alignItems="center" gap={1}>
                  {dateLoading && (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  )}
                  <IconButton 
                    size="small" 
                    onClick={() => handleDateChange('prev')}
                    title="Previous day"
                    disabled={dateLoading}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Button
                    size="small"
                    onClick={() => handleDateChange('today')}
                    disabled={selectedDate.toDateString() === new Date().toDateString() || dateLoading}
                  >
                    Today
                  </Button>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDateChange('next')}
                    title="Next day"
                    disabled={dateLoading}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setNewEventDate(formatDateLocal(selectedDate));
                      setNewEventEndDate(formatDateLocal(selectedDate));
                      setNewEventDialog(true);
                    }}
                    title="Add event"
                    color="primary"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0, position: 'relative' }}>
              {dateLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              {events.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No events scheduled for {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {events.map((event, index) => {
                    const status = getEventStatus(event);
                    return (
                      <React.Fragment key={event.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          button
                          onClick={() => handleEventClick(event)}
                        >
                          <ListItemIcon>
                            {event.summary?.startsWith('🚗 Travel to') ? (
                              <DirectionsCarIcon color="primary" />
                            ) : event.location || eventLocations[event.id] ? (
                              <PlaceIcon />
                            ) : (
                              <ScheduleIcon />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              event.recurringEventId ? (
                                <Tooltip title="This is a recurring event" arrow>
                                  <span>{event.summary}</span>
                                </Tooltip>
                              ) : (
                                event.summary
                              )
                            }
                            secondary={formatEventTime(event)}
                            primaryTypographyProps={{
                              style: {
                                textDecoration: status === 'past' ? 'line-through' : 'none',
                                opacity: status === 'past' ? 0.6 : 1
                              }
                            }}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {status === 'ongoing' && (
                                <Chip label="Now" color="primary" size="small" />
                              )}
                              {status === 'upcoming' && (
                                <Chip label="Upcoming" color="default" size="small" />
                              )}
                              {event.summary?.startsWith('🚗 Travel to') && (
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickDeleteTravel(event);
                                  }}
                                  title="Delete travel event"
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Tasks List */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Tasks"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<CheckCircleIcon />}
              action={
                <IconButton onClick={() => setNewTaskDialog(true)}>
                  <AddIcon />
                </IconButton>
              }
            />
            <Divider />
            {/* Task Lists Dropdown */}
            {taskLists.length > 0 && (
              <Box sx={{ p: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="task-list-select-label">Task List</InputLabel>
                  <Select
                    labelId="task-list-select-label"
                    id="task-list-select"
                    value={selectedTaskList}
                    label="Task List"
                    onChange={(e) => handleTaskListChange(e.target.value)}
                  >
                    {taskLists.map((list) => (
                      <MenuItem key={list.id} value={list.id}>
                        {list.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Task Filter Badges */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`All (${getTaskFilterCount('all')})`}
                    onClick={() => setTaskFilter('all')}
                    color={taskFilter === 'all' ? 'primary' : 'default'}
                    variant={taskFilter === 'all' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip
                    label={`Today (${getTaskFilterCount('today')})`}
                    onClick={() => setTaskFilter('today')}
                    color={taskFilter === 'today' ? 'primary' : 'default'}
                    variant={taskFilter === 'today' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip
                    label={`Tomorrow (${getTaskFilterCount('tomorrow')})`}
                    onClick={() => setTaskFilter('tomorrow')}
                    color={taskFilter === 'tomorrow' ? 'primary' : 'default'}
                    variant={taskFilter === 'tomorrow' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip
                    label={`Past Due (${getTaskFilterCount('pastdue')})`}
                    onClick={() => setTaskFilter('pastdue')}
                    color={taskFilter === 'pastdue' ? 'error' : 'default'}
                    variant={taskFilter === 'pastdue' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip
                    label={`No Due Date (${getTaskFilterCount('nodue')})`}
                    onClick={() => setTaskFilter('nodue')}
                    color={taskFilter === 'nodue' ? 'primary' : 'default'}
                    variant={taskFilter === 'nodue' ? 'filled' : 'outlined'}
                    size="small"
                  />
                </Box>
              </Box>
            )}
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {(() => {
                const filteredTasks = getFilteredTasks();
                if (filteredTasks.length === 0) {
                  return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        {taskFilter === 'all' ? 'No tasks yet' : 
                         taskFilter === 'nodue' ? 'No tasks without due dates' :
                         taskFilter === 'pastdue' ? 'No past due tasks' :
                         `No ${taskFilter} tasks`}
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setNewTaskDialog(true)}
                        sx={{ mt: 2 }}
                      >
                        Add Task
                      </Button>
                    </Box>
                  );
                }
                
                return (
                  <List>
                    {filteredTasks.map((task, index) => (
                      <React.Fragment key={task.id}>
                        {index > 0 && (
                          <Divider 
                            sx={{
                              borderColor: dragOverIndex === index ? 'primary.main' : 'divider',
                              borderWidth: dragOverIndex === index ? 2 : 1,
                              transition: 'all 0.2s'
                            }}
                          />
                        )}
                        <ListItem
                          draggable
                          onDragStart={(e) => handleDragStart(e, task, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          sx={{
                            cursor: 'move',
                            backgroundColor: dragOverIndex === index ? 'action.hover' : 'transparent',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <DragIndicatorIcon 
                              sx={{ 
                                color: 'text.secondary',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' }
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={task.completed}
                              onChange={() => handleToggleTask(task.id)}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              task.isRecurring && task.recurrenceInfo ? (
                                <Tooltip title={`Recurring: ${task.recurrenceInfo}`} arrow>
                                  <span>{task.title}</span>
                                </Tooltip>
                              ) : (
                                task.title
                              )
                            }
                            secondary={
                              <span>
                                {task.dueDate ? (() => {
                                  // Add T12:00:00 to ensure the date is interpreted in local timezone
                                  const dueDate = new Date(task.dueDate + 'T12:00:00');
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  dueDate.setHours(0, 0, 0, 0);
                                  
                                  const diffTime = dueDate.getTime() - today.getTime();
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  
                                  let dateText = dueDate.toLocaleDateString();
                                  if (diffDays === 0) {
                                    dateText = 'Due Today';
                                  } else if (diffDays === 1) {
                                    dateText = 'Due Tomorrow';
                                  } else if (diffDays === -1) {
                                    dateText = 'Due Yesterday';
                                  } else if (diffDays < -1) {
                                    dateText = `${Math.abs(diffDays)} days overdue`;
                                  } else {
                                    dateText = `Due ${dueDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
                                  }
                                  
                                  return dateText;
                                })() : ''}
                                {task.isRecurring && !task.dueDate && ' • Recurring task'}
                              </span>
                            }
                            secondaryTypographyProps={{
                              color: task.dueDate && new Date(task.dueDate + 'T12:00:00') < new Date() ? 'error' : 'textSecondary'
                            }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditTask(task)}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteTask(task.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Task Dialog */}
      <Dialog open={newTaskDialog} onClose={() => setNewTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            fullWidth
            variant="outlined"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !newTaskDueDate && handleAddTask()}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Due Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newTaskDueDate}
            onChange={(e) => setNewTaskDueDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewTaskDialog(false);
            setNewTaskTitle('');
            setNewTaskDueDate('');
          }}>Cancel</Button>
          <Button onClick={handleAddTask} variant="contained">
            Add Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={editTaskDialog} onClose={() => setEditTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            fullWidth
            variant="outlined"
            value={editTaskTitle}
            onChange={(e) => setEditTaskTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {/* Task List Selector */}
          {taskLists.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-task-list-label">Task List</InputLabel>
              <Select
                labelId="edit-task-list-label"
                value={editTaskListId}
                label="Task List"
                onChange={(e) => setEditTaskListId(e.target.value)}
              >
                {taskLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Due Date: {editTaskDueDate ? new Date(editTaskDueDate + 'T12:00:00').toLocaleDateString() : 'None'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<TodayIcon />}
                label="Today"
                onClick={() => setQuickDate('today')}
                variant={editTaskDueDate === formatDateLocal(new Date()) ? 'filled' : 'outlined'}
                color="primary"
                clickable
              />
              <Chip
                icon={<TomorrowIcon />}
                label="Tomorrow"
                onClick={() => setQuickDate('tomorrow')}
                variant={(() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  return editTaskDueDate === formatDateLocal(tomorrow) ? 'filled' : 'outlined';
                })()}
                color="primary"
                clickable
              />
              <Chip
                icon={<DateRangeIcon />}
                label="Pick Date"
                onClick={() => setDatePickerOpen(true)}
                variant="outlined"
                color="primary"
                clickable
              />
              {editTaskDueDate && (
                <Chip
                  label="Clear"
                  onClick={() => setEditTaskDueDate('')}
                  variant="outlined"
                  color="secondary"
                  clickable
                />
              )}
            </Box>
          </Box>

          {datePickerOpen && (
            <TextField
              margin="dense"
              label="Select Date"
              type="date"
              fullWidth
              variant="outlined"
              value={editTaskDueDate}
              onChange={(e) => {
                setEditTaskDueDate(e.target.value);
                setDatePickerOpen(false);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0],
                max: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString().split('T')[0]
              }}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditTaskDialog(false);
            setEditingTask(null);
            setEditTaskTitle('');
            setEditTaskDueDate('');
            setEditTaskListId('@default');
            setDatePickerOpen(false);
          }}>Cancel</Button>
          <Button onClick={handleSaveEditTask} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={newEventDialog} onClose={() => setNewEventDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Show success message when times are updated */}
            {timeUpdatedMessage && (
              <Alert 
                severity="success" 
                onClose={() => setTimeUpdatedMessage('')}
                sx={{ mb: 1 }}
              >
                {timeUpdatedMessage}
              </Alert>
            )}
            
            {/* Event Type */}
            <FormControl fullWidth required>
              <InputLabel id="event-type-label">Event Type</InputLabel>
              <Select
                labelId="event-type-label"
                value={newEventType}
                label="Event Type"
                onChange={(e) => handleEventTypeChange(e.target.value)}
              >
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Event Title */}
            <TextField
              label="Event Title"
              fullWidth
              required
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
              placeholder={newEventType === 'call' ? 'Call with...' : 'Meeting title...'}
            />

            {/* Description */}
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newEventDescription}
              onChange={(e) => setNewEventDescription(e.target.value)}
            />

            {/* Date and Time */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                required
                value={newEventDate}
                onChange={(e) => {
                  setNewEventDate(e.target.value);
                  // Also update end date if it's before the new start date
                  if (!newEventEndDate || e.target.value > newEventEndDate) {
                    setNewEventEndDate(e.target.value);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Start Time"
                type="time"
                required
                value={newEventStartTime}
                onChange={(e) => setNewEventStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* End date and time */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="End Date"
                type="date"
                required
                value={newEventEndDate}
                onChange={(e) => setNewEventEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: newEventDate }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="End Time"
                type="time"
                required
                value={newEventEndTime}
                onChange={(e) => setNewEventEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Leave Early dropdown for out of office meetings */}
            {newEventType === 'out_of_office' && (
              <>
                <FormControl fullWidth>
                  <InputLabel id="leave-early-label">Leave Early Buffer</InputLabel>
                  <Select
                    labelId="leave-early-label"
                    value={leaveEarlyMinutes}
                    label="Leave Early Buffer"
                    onChange={(e) => setLeaveEarlyMinutes(e.target.value as number)}
                  >
                    <MenuItem value={5}>5 minutes</MenuItem>
                    <MenuItem value={10}>10 minutes</MenuItem>
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={20}>20 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Travel time will be calculated automatically and added to this buffer
                  </Typography>
                </FormControl>
                
                {/* Office Address Warning */}
                {hasOfficeAddress === false && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      No office address set
                    </Typography>
                    <Typography variant="caption">
                      Travel time will not be calculated without an office address. 
                      Please set your office address in your profile settings to enable automatic travel time calculation.
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            {/* Location */}
            {newEventType === 'zoom' || newEventType === 'meet' ? (
              <TextField
                label="Location"
                fullWidth
                value={newEventLocation}
                onChange={(e) => setNewEventLocation(e.target.value)}
                placeholder={
                  newEventType === 'zoom' ? 'Zoom link...' : 
                  newEventType === 'meet' ? 'Google Meet link...' : 
                  'Location...'
                }
              />
            ) : (
              <GooglePlacesAutocompleteNative
                value={newEventLocation}
                onChange={setNewEventLocation}
                onPlaceSelected={(place) => {
                  console.log('Place selected:', place);
                  if (place.formatted_address) {
                    setNewEventLocation(place.formatted_address);
                  }
                }}
                label="Location"
                placeholder="Enter address..."
                fullWidth
              />
            )}

            {/* Attendees */}
            <Autocomplete
              multiple
              freeSolo
              options={availableContacts}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.email || option.name || '';
              }}
              value={newEventAttendees}
              onChange={(event, newValue) => {
                setNewEventAttendees(newValue.map(v => 
                  typeof v === 'string' ? v : v.email
                ));
              }}
              onInputChange={(event, newInputValue) => {
                setContactsSearch(newInputValue);
                if (newInputValue.length >= 2) {
                  searchContacts(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Attendees"
                  placeholder="Add attendees by email..."
                  helperText="Type email addresses or search contacts"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">
                      {typeof option === 'string' ? option : option.name}
                    </Typography>
                    {typeof option !== 'string' && option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Busy/Free toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={newEventTransparency === 'transparent'}
                onChange={(e) => setNewEventTransparency(e.target.checked ? 'transparent' : 'opaque')}
              />
            }
            label="Mark as free time"
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            {newEventTransparency === 'transparent' 
              ? 'This time will show as available in your calendar' 
              : 'This time will show as busy in your calendar'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewEventDialog(false);
            setNewEventTitle('');
            setNewEventType('');
            setNewEventDescription('');
            setNewEventDate('');
            setNewEventEndDate('');
            setNewEventStartTime('');
            setNewEventEndTime('');
            setNewEventLocation('');
            setNewEventAttendees([]);
            setNewEventTransparency('opaque');
            setContactsSearch('');
            setAvailableContacts([]);
            setLeaveEarlyMinutes(15);
            setTimeUpdatedMessage('');
          }}>Cancel</Button>
          <Button 
            onClick={handleAddEvent} 
            variant="contained"
            disabled={!newEventTitle || !newEventType || !newEventDate || !newEventStartTime || savingEvent}
          >
            {savingEvent ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog 
        open={editEventDialog} 
        onClose={() => {
          if (!savingEvent) {
            setEditEventDialog(false);
            // Reset form fields
            setEditEventTitle('');
            setEditEventDescription('');
            setEditEventLocation('');
            setEditEventDate('');
            setEditEventStartTime('');
            setEditEventEndTime('');
            setEditEventAttendees([]);
            setEditingEvent(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Event</Typography>
            <Box>
              {editingEvent?.extendedProperties?.private?.eventType === 'out_of_office' && (
                <Chip 
                  label="Out of Office" 
                  size="small" 
                  color="primary" 
                  sx={{ mr: 1 }}
                />
              )}
              <IconButton 
                edge="end" 
                color="error" 
                onClick={handleDeleteEvent}
                disabled={deletingEvent}
                title="Delete event"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Event Title */}
            <TextField
              label="Event Title"
              value={editEventTitle}
              onChange={(e) => setEditEventTitle(e.target.value)}
              fullWidth
              required
            />

            {/* Description */}
            <TextField
              label="Description"
              value={editEventDescription}
              onChange={(e) => setEditEventDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            {/* Start Date field */}
            <TextField
              label="Start Date"
              type="date"
              value={editEventDate}
              onChange={(e) => {
                setEditEventDate(e.target.value);
                // Also update end date if it's before the new start date
                if (!editEventEndDate || e.target.value > editEventEndDate) {
                  setEditEventEndDate(e.target.value);
                }
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />

            {/* Time fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={editEventStartTime}
                onChange={(e) => setEditEventStartTime(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            {/* End date and time */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="End Date"
                type="date"
                value={editEventEndDate}
                onChange={(e) => setEditEventEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: editEventDate }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                value={editEventEndTime}
                onChange={(e) => setEditEventEndTime(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            {/* Location */}
            <Box>
              {editEventLocation && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Current Address:</strong> {editEventLocation}
                </Typography>
              )}
              <GooglePlacesAutocompleteNative
                value=""
                onChange={setEditEventLocation}
                onPlaceSelected={(place) => {
                  if (place.formatted_address) {
                    setEditEventLocation(place.formatted_address);
                  }
                }}
                label="Change Location"
                placeholder="Search for a new address..."
                fullWidth
              />
            </Box>

            {/* Office Address Warning for Out of Office Events */}
            {editingEvent?.extendedProperties?.private?.eventType === 'out_of_office' && hasOfficeAddress === false && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  No office address set
                </Typography>
                <Typography variant="caption">
                  Travel time will not be calculated without an office address. 
                  Please set your office address in your profile settings to enable automatic travel time calculation.
                </Typography>
              </Alert>
            )}

            {/* Attendees */}
            <Autocomplete
              multiple
              freeSolo
              options={availableContacts}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.email || option.name || '';
              }}
              value={editEventAttendees}
              onChange={(event, newValue) => {
                setEditEventAttendees(newValue.map(v => 
                  typeof v === 'string' ? v : v.email
                ));
              }}
              onInputChange={(event, newInputValue) => {
                setContactsSearch(newInputValue);
                if (newInputValue.length >= 2) {
                  searchContacts(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Attendees"
                  placeholder="Add attendees by email..."
                  helperText="Type email addresses or search contacts"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">
                      {typeof option === 'string' ? option : option.name}
                    </Typography>
                    {typeof option !== 'string' && option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Busy/Free toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={editEventTransparency === 'transparent'}
                onChange={(e) => setEditEventTransparency(e.target.checked ? 'transparent' : 'opaque')}
              />
            }
            label="Mark as free time"
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            {editEventTransparency === 'transparent' 
              ? 'This time will show as available in your calendar' 
              : 'This time will show as busy in your calendar'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteEvent}
            color="error"
            disabled={savingEvent || deletingEvent}
            sx={{ mr: 'auto' }}
          >
            {deletingEvent ? 'Deleting...' : 'Delete'}
          </Button>
          <Button 
            onClick={() => {
              setEditEventDialog(false);
              setEditingEvent(null);
            }}
            disabled={savingEvent || deletingEvent}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateEvent} 
            variant="contained"
            disabled={!editEventTitle || savingEvent || deletingEvent}
          >
            {savingEvent ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Dialog */}
      <Dialog 
        open={conflictDialog} 
        onClose={() => setConflictDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DirectionsCarIcon color="warning" />
            <Typography variant="h6">Travel Time Required</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {conflictData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="warning">
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {conflictData.message}
                </Typography>
              </Alert>
              
              {conflictData.suggestedTimes && (() => {
                // Calculate the correct end time based on original duration
                const originalStartTime = new Date(`2000-01-01T${newEventStartTime}:00`);
                const originalEndTime = new Date(`2000-01-01T${newEventEndTime}:00`);
                let durationMs = originalEndTime.getTime() - originalStartTime.getTime();
                
                // Handle case where end time is on next day
                if (durationMs < 0) {
                  durationMs += 24 * 60 * 60 * 1000; // Add 24 hours
                }
                
                // Use the end time provided by the backend
                const suggestedEndTime = conflictData.suggestedTimes.endTime || (() => {
                  // Fallback: Calculate new end time if not provided
                  const newStartTime = new Date(`2000-01-01T${conflictData.suggestedTimes.startTime}:00`);
                  const newEndTimeMs = newStartTime.getTime() + durationMs;
                  const newEndTime = new Date(newEndTimeMs);
                  
                  // Format time as HH:MM
                  const hours = newEndTime.getHours().toString().padStart(2, '0');
                  const minutes = newEndTime.getMinutes().toString().padStart(2, '0');
                  return `${hours}:${minutes}`;
                })();
                
                // Extract travel time from conflict data if available
                const travelMinutes = conflictData.travelMinutes || 0;
                
                return (
                  <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Recommended Appointment Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {conflictData.suggestedTimes.startTime} - {suggestedEndTime}
                    </Typography>
                    {travelMinutes > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        • Travel time required: {travelMinutes} minutes
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      • Same duration as originally requested
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      This time accounts for travel from your previous appointment
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConflictDialog(false)}
          >
            Cancel
          </Button>
          {conflictData?.suggestedTimes && (
            <Button 
              variant="contained"
              color="primary"
              onClick={() => {
                // Apply the suggested start and end times from the backend
                const suggestedStart = conflictData.suggestedTimes.startTime;
                const suggestedEnd = conflictData.suggestedTimes.endTime || (() => {
                  // Fallback: Calculate end time if not provided
                  const originalStartTime = new Date(`2000-01-01T${newEventStartTime}:00`);
                  const originalEndTime = new Date(`2000-01-01T${newEventEndTime}:00`);
                  let durationMs = originalEndTime.getTime() - originalStartTime.getTime();
                  
                  // Handle case where end time is on next day
                  if (durationMs < 0) {
                    durationMs += 24 * 60 * 60 * 1000; // Add 24 hours
                  }
                  
                  // Calculate new end time
                  const newStartTime = new Date(`2000-01-01T${suggestedStart}:00`);
                  const newEndTimeMs = newStartTime.getTime() + durationMs;
                  const newEndTime = new Date(newEndTimeMs);
                  
                  // Format times as HH:MM
                  const hours = newEndTime.getHours().toString().padStart(2, '0');
                  const minutes = newEndTime.getMinutes().toString().padStart(2, '0');
                  return `${hours}:${minutes}`;
                })();
                
                setNewEventStartTime(suggestedStart);
                setNewEventEndTime(suggestedEnd);
                setConflictDialog(false);
                setConflictData(null);
                
                // Show success message
                setTimeUpdatedMessage(`Appointment time updated to ${suggestedStart} - ${suggestedEnd} to allow for travel time`);
              }}
            >
              Use Recommended Time
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;